import { registerSW } from 'virtual:pwa-register';

export interface PwaHandlers {
  onNeedRefresh?: (update: () => Promise<void>) => void;
  onOfflineReady?: () => void;
}

/** 何度 setupPwa を呼んでも SW 登録は 1 回だけ。 */
let started = false;
let registrationRef: ServiceWorkerRegistration | undefined;
let applyUpdate: ((reloadPage?: boolean) => Promise<void>) | undefined;

const CHECK_INTERVAL_MS = 60_000;

/**
 * Service Worker の登録。
 *
 * 自動更新にしていない理由: エミュレーション中に SW が入れ替わると
 * ページがリロードされてプレイ内容が飛ぶ。更新は必ずユーザーに確認する。
 *
 * ただし更新の「検知」は起動直後＋定期＋フォアグラウンド復帰で走らせる。
 * これを怠ると iOS PWA が古い precache のまま固まる。
 */
export function setupPwa(handlers: PwaHandlers = {}): void {
  if (!('serviceWorker' in navigator) || started) return;
  started = true;

  applyUpdate = registerSW({
    immediate: true,
    onNeedRefresh() {
      handlers.onNeedRefresh?.(() => (applyUpdate ? applyUpdate(true) : Promise.resolve()));
    },
    onOfflineReady() {
      handlers.onOfflineReady?.();
    },
    onRegisteredSW(_url, registration) {
      registrationRef = registration;
      if (!registration) return;

      const check = () => {
        void registration.update().catch((e) => {
          console.warn('[pwa] update check failed', e);
        });
      };

      // 起動直後にもう一度（install 直後のレース対策）
      window.setTimeout(check, 1500);
      window.setInterval(check, CHECK_INTERVAL_MS);

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
      });
      window.addEventListener('focus', check);
      window.addEventListener('online', check);
    },
    onRegisterError(error) {
      console.warn('[pwa] service worker registration failed', error);
    },
  });
}

/** 設定画面などから明示的に更新を取りに行く。 */
export async function checkForPwaUpdate(): Promise<'checking' | 'unsupported'> {
  if (!registrationRef) {
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        registrationRef = reg;
        await reg.update();
        return 'checking';
      }
    }
    return 'unsupported';
  }
  await registrationRef.update();
  return 'checking';
}

/** iOS Safari の PWA (ホーム画面から起動) かどうか */
export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    // iOS 独自プロパティ
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function isIos(): boolean {
  const ua = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ はデスクトップ Safari を名乗る
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

export function isTouchDevice(): boolean {
  return navigator.maxTouchPoints > 0 || window.matchMedia('(pointer: coarse)').matches;
}

/**
 * iOS で 100vh がずれる問題への保険。
 * dvh 対応ブラウザでは何もしなくてよいが、古い iOS 向けに実測値を流し込む。
 */
export function installViewportHeightFix(): () => void {
  const supportsDvh = CSS.supports?.('height', '100dvh') ?? false;
  if (supportsDvh) return () => undefined;

  const apply = () => {
    document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
  };
  apply();
  window.addEventListener('resize', apply);
  window.addEventListener('orientationchange', apply);
  return () => {
    window.removeEventListener('resize', apply);
    window.removeEventListener('orientationchange', apply);
  };
}
